import React, { useState, useEffect, useRef } from "react";
import { Select, Button, message } from "antd";
import MultiModalSelector from "../../components/form/MultiModalSelector";
import api from "../../utils/axios";
import { AgGridReact } from "ag-grid-react";
import useLang from "../../hooks/useLang";
import classes from "./UserCashRegister.module.css";
import { cashRegistersLookup } from "../../lib/cachedLookups";
import useLookup from "../../hooks/useLookup";
import { CashRegister } from "../../types/Lookups";
import DefaultSelect from "../../components/form/DefaultSelect";
import { userRoles } from "../../types/enums";
import { useMediaQuery } from "@mui/material";

const { Option } = Select;

interface CashRegisterUser {
	id: number;
	description: string;
	userId: number;
	username: string;
}

const UserCashRegisterManager: React.FC = () => {
	const [users, setUsers] = useState<any[]>([]);
	const [selectedUser, setSelectedUser] = useState<number | null>(null);
	const { data: cashRegisters = [], error: cashRegistersFailed } =
		useLookup(cashRegistersLookup);
	const [cashRegistersUsers, setCashRegistersUsers] = useState<
		CashRegisterUser[]
	>([]);
	const [selectedCashRegisters, setSelectedCashRegisters] = useState<
		number[]
	>([]);
	const multiModalSelectorRef = useRef<any>(null);

	useEffect(() => {
		fetchUsers();
		fetchAllCashRegistersUsers();
	}, []);
	const { strings } = useLang("userCashRegister");

	useEffect(() => {
		if (cashRegistersFailed) message.error(strings?.loadCashRegistersError);
	}, [cashRegistersFailed, strings]);
	const fetchUsers = async () => {
		try {
			const response = await api.get("/user");
			if (response.status === 200) {
				const userOptions = response.data
					.filter(
						(item: any) =>
							item.userRoleId === userRoles.ADMIN ||
							item.userRoleId === userRoles.FINANCE
					)
					.map((user: any) => ({
						value: user.id,
						label: `${user.firstName} ${user.lastName} (${user.userRoleDescription})`,
					}));
				setUsers(userOptions);
			}
		} catch (error) {
			message.error(strings?.loadUsersError);
		}
	};

	const fetchAllCashRegistersUsers = async () => {
		setCashRegistersUsers(await fetchCashRegistersUsers(null));
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	const columnDefs = [
		{
			headerName: "ID",
			field: "id",
			width: 140,
			suppressSizeToFit: isSmallScreen,
		},
		{
			headerName: "Cash register",
			field: "description",
			width: 180,
			suppressSizeToFit: isSmallScreen,
		},
		{
			headerName: "User ID",
			field: "userId",
			width: 100,
			suppressSizeToFit: isSmallScreen,
		},
		{
			headerName: "Username",
			field: "username",
			width: 140,
			suppressSizeToFit: isSmallScreen,
		},
	];

	const fetchCashRegistersUsers = async (userId: number | null) => {
		const response = await api.get("/UserCashRegister/list", {
			params: { userId },
		});
		return response.data;
	};

	const handleUserChange = async (value: number) => {
		setSelectedUser(value);
		try {
			const data = await fetchCashRegistersUsers(value);
			const ids = data.map((e: CashRegister) => e.id);
			setSelectedCashRegisters(ids);

			if (multiModalSelectorRef.current) {
				multiModalSelectorRef.current.setRows(ids);
			}
		} catch (error) {
			message.error(strings?.loadUserCashRegistersError);
			console.log(error);
		}
	};

	const handleCashRegisterSelection = (selectedRows: CashRegister[]) => {
		setSelectedCashRegisters(selectedRows.map((e) => e.id));
	};

	const handleMerge = async () => {
		if (selectedUser === null) {
			message.error(strings?.pleaseSelectUserError);
			return;
		}
		const body = {
			userId: selectedUser,
			cashRegisterIds: selectedCashRegisters,
		};
		try {
			await api.post("/UserCashRegister/merge", body);
			await fetchAllCashRegistersUsers();
			message.success(strings?.updateCashRegistersSuccess);
		} catch (error) {
			message.error(strings?.updateCashRegistersError);
		}
	};

	return (
		<div className={classes.mainContainer}>
			<div className={classes.heroContainer}>
				<div className="flex flex-col gap-6 ">
					<div className="flex flex-wrap items-center gap-4">
						<div className="flex flex-col gap-1">
							{/* <p>{strings?.selectUser}</p> */}
							<DefaultSelect
								label={strings?.selectUser}
								placeholder={strings?.selectUserPlaceholder}
								className={classes.input}
								onChange={handleUserChange}
								flexDirection="col"
								alignCenter="start"
								value={selectedUser}
								options={users}
							/>
						</div>
						<MultiModalSelector
							ref={multiModalSelectorRef}
							tableData={cashRegisters}
							onRowsSelected={handleCashRegisterSelection}
							onRowsCleared={() => setSelectedCashRegisters([])}
							buttonLabel={
								strings?.selectCashRegistersButtonLabel
							}
							primaryKey="id"
							value={selectedCashRegisters}
						/>
					</div>
					<Button
						type="default"
						onClick={handleMerge}
						disabled={selectedUser === null}
						className={classes.button}
					>
						{strings?.saveButton}
					</Button>
				</div>
				<div
					className="ag-theme-alpine"
					style={{ height: 600, width: "100%" }}
				>
					<AgGridReact
						rowData={cashRegistersUsers}
						columnDefs={columnDefs}
						pagination={true}
						paginationPageSize={10}
					/>
				</div>
			</div>
		</div>
	);
};

export default UserCashRegisterManager;
